import React, { useState, useEffect } from 'react'
import {
  Trophy, TrendingUp, Award, Star, Crown,
  Settings, Plus, Edit2, Trash2, Gift,
  Medal, Target, Zap, DollarSign, X, CheckCircle, Clock, AlertCircle,
  Calendar, History, Play, Pause
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface LeaderboardEntry {
  id: string
  user_id: string
  display_name: string
  username: string
  color_theme: string
  total_points: number
  lifetime_points?: number
  chores_completed: number
  streak_days: number
  perfect_weeks: number
  bonus_earned: number
  rank: number
  rank_change: number
}

interface LeaderboardReward {
  id: string
  position: number
  timeframe: 'weekly' | 'monthly' | 'yearly'
  reward_type: 'points' | 'privilege' | 'prize'
  reward_value: string
  description: string
  is_active: boolean
}

interface RewardSettings {
  auto_distribute_weekly: boolean
  auto_distribute_monthly: boolean
  auto_distribute_yearly: boolean
  distribution_day_of_week: number
  distribution_hour: number
  min_participants: number
  min_points_threshold: number
  notify_on_distribution: boolean
}

interface Distribution {
  id: string
  user_id: string
  user_name: string
  timeframe: string
  period_label: string
  position: number
  total_points: number
  points_awarded: number | null
  prize_description: string | null
  prize_fulfilled: boolean
  prize_fulfilled_at: string | null
  distributed_at: string
}

interface PendingPeriod {
  timeframe: string
  period_start: string
  period_end: string
  period_label: string
  participants: number
  top_performers: Array<{
    position: number
    user_id: string
    display_name: string
    total_points: number
  }>
}

interface PointBreakdown {
  user_id: string
  display_name: string
  chore_points: number
  bonus_points: number
  deductions: number
  redemptions: number
  conversions: number
  total_earned: number
  available_points: number
  recent_activities: Array<{
    date: string
    description: string
    points: number
    type: 'earned' | 'deducted' | 'redeemed' | 'converted'
  }>
}

type TabType = 'leaderboard' | 'rewards' | 'history' | 'settings'

export const LeaderboardManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('leaderboard')
  const [timeframe, setTimeframe] = useState<'week' | 'month' | 'year' | 'all'>('week')
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [rewards, setRewards] = useState<LeaderboardReward[]>([])
  const [settings, setSettings] = useState<RewardSettings | null>(null)
  const [distributions, setDistributions] = useState<Distribution[]>([])
  const [pendingPeriods, setPendingPeriods] = useState<PendingPeriod[]>([])
  const [loading, setLoading] = useState(true)
  const [showRewardModal, setShowRewardModal] = useState(false)
  const [editingReward, setEditingReward] = useState<LeaderboardReward | null>(null)
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const [pointBreakdown, setPointBreakdown] = useState<PointBreakdown | null>(null)
  const [loadingBreakdown, setLoadingBreakdown] = useState(false)
  const [distributing, setDistributing] = useState(false)
  const [newReward, setNewReward] = useState<Partial<LeaderboardReward>>({
    position: 1,
    timeframe: 'weekly',
    reward_type: 'points',
    reward_value: '',
    description: '',
    is_active: true
  })

  useEffect(() => {
    loadLeaderboard()
    loadRewards()
    loadSettings()
    loadDistributions()
    loadPendingPeriods()

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadLeaderboard(true)
    }, 30000)

    return () => clearInterval(interval)
  }, [timeframe])

  // Background refreshes fail quietly; only the first load reports an error.
  const loadLeaderboard = async (background = false) => {
    setLoading(true)
    try {
      const data = await apiClient.get(`/api/leaderboard?timeframe=${timeframe}`)
      setLeaderboard(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to load leaderboard:', error)
      if (!background) toast.error('Failed to load leaderboard')
    } finally {
      setLoading(false)
    }
  }

  const loadRewards = async () => {
    try {
      const data = await apiClient.get('/api/leaderboard/rewards')
      setRewards(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to load rewards:', error)
    }
  }

  const loadSettings = async () => {
    try {
      const response = await apiClient.get('/api/rewards/settings')
      if (response.success && response.data) {
        setSettings(response.data)
      }
    } catch (error) {
      console.error('Failed to load settings:', error)
    }
  }

  const loadDistributions = async () => {
    try {
      const response = await apiClient.get('/api/rewards/distributions')
      if (response.success && response.data) {
        setDistributions(response.data)
      }
    } catch (error) {
      console.error('Failed to load distributions:', error)
    }
  }

  const loadPendingPeriods = async () => {
    try {
      const response = await apiClient.get('/api/rewards/pending')
      if (response.success && response.data) {
        setPendingPeriods(response.data)
      }
    } catch (error) {
      console.error('Failed to load pending periods:', error)
    }
  }

  const handleCreateReward = async () => {
    try {
      await apiClient.post('/api/leaderboard/rewards', newReward)
      toast.success('Reward created successfully')
      setShowRewardModal(false)
      setNewReward({
        position: 1,
        timeframe: 'weekly',
        reward_type: 'points',
        reward_value: '',
        description: '',
        is_active: true
      })
      loadRewards()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create reward')
    }
  }

  const handleUpdateReward = async (rewardId: string, updates: Partial<LeaderboardReward>) => {
    try {
      await apiClient.put(`/api/leaderboard/rewards/${rewardId}`, updates)
      toast.success('Reward updated successfully')
      setEditingReward(null)
      loadRewards()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update reward')
    }
  }

  const handleDeleteReward = async (rewardId: string) => {
    if (!confirm('Are you sure you want to delete this reward?')) return

    try {
      await apiClient.delete(`/api/leaderboard/rewards/${rewardId}`)
      toast.success('Reward deleted successfully')
      loadRewards()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete reward')
    }
  }

  const handleDistributeRewards = async (tf: string) => {
    setDistributing(true)
    try {
      const response = await apiClient.post('/api/rewards/distribute', { timeframe: tf })
      if (response.success) {
        toast.success(`Rewards distributed for ${tf} period!`)
        loadDistributions()
        loadPendingPeriods()
        loadLeaderboard()
      } else {
        toast.error(response.error || 'Failed to distribute rewards')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to distribute rewards')
    } finally {
      setDistributing(false)
    }
  }

  const handleUpdateSettings = async (updates: Partial<RewardSettings>) => {
    try {
      const response = await apiClient.put('/api/rewards/settings', updates)
      if (response.success) {
        toast.success('Settings updated')
        setSettings(prev => prev ? { ...prev, ...updates } : null)
      }
    } catch (error: any) {
      toast.error('Failed to update settings')
    }
  }

  const handleFulfillPrize = async (distributionId: string) => {
    try {
      const response = await apiClient.post(`/api/rewards/distributions/${distributionId}/fulfill`)
      if (response.success) {
        toast.success('Prize marked as fulfilled')
        loadDistributions()
      }
    } catch (error: any) {
      toast.error('Failed to fulfill prize')
    }
  }

  const loadPointBreakdown = async (userId: string) => {
    setLoadingBreakdown(true)
    try {
      const breakdown = await apiClient.get(`/api/users/${userId}/points-breakdown`)
      setPointBreakdown(breakdown)
      setSelectedUser(userId)
    } catch (error) {
      console.error('Failed to load point breakdown:', error)
      toast.error('Failed to load point breakdown')
    } finally {
      setLoadingBreakdown(false)
    }
  }

  const getMedal = (position: number) => {
    if (position === 1) return <Crown className="w-6 h-6 text-yellow-500" />
    if (position === 2) return <Medal className="w-6 h-6 text-gray-400" />
    if (position === 3) return <Medal className="w-6 h-6 text-orange-600" />
    return <span className="w-6 h-6 flex items-center justify-center text-gray-600 font-bold">{position}</span>
  }

  const getRankChange = (change: number) => {
    if (change > 0) return <span className="text-green-600 text-sm">↑{change}</span>
    if (change < 0) return <span className="text-red-600 text-sm">↓{Math.abs(change)}</span>
    return <span className="text-gray-400 text-sm">-</span>
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Leaderboard & Rewards</h2>
        <p className="text-gray-600">Track performance and manage competitive rewards</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
            { id: 'rewards', label: 'Reward Config', icon: Gift },
            { id: 'history', label: 'Distribution History', icon: History },
            { id: 'settings', label: 'Auto-Distribution', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <>
          {/* Pending Periods Alert */}
          {pendingPeriods.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3" />
                <div className="flex-1">
                  <h4 className="font-medium text-amber-800">Pending Reward Distributions</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    {pendingPeriods.length} period(s) have completed and are ready for reward distribution.
                  </p>
                  <div className="mt-3 space-y-2">
                    {pendingPeriods.map(period => (
                      <div key={`${period.timeframe}-${period.period_start}`} className="flex items-center justify-between bg-white rounded p-3">
                        <div>
                          <span className="font-medium capitalize">{period.timeframe}</span>
                          <span className="text-gray-500 ml-2">- {period.period_label}</span>
                          <span className="text-sm text-gray-500 ml-2">({period.participants} participants)</span>
                        </div>
                        <button
                          onClick={() => handleDistributeRewards(period.timeframe)}
                          disabled={distributing}
                          className="px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 text-sm"
                        >
                          {distributing ? 'Distributing...' : 'Distribute Now'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setTimeframe('week')}
                  className={`px-4 py-2 rounded-lg ${
                    timeframe === 'week' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                  }`}
                >
                  This Week
                </button>
                <button
                  onClick={() => setTimeframe('month')}
                  className={`px-4 py-2 rounded-lg ${
                    timeframe === 'month' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                  }`}
                >
                  This Month
                </button>
                <button
                  onClick={() => setTimeframe('year')}
                  className={`px-4 py-2 rounded-lg ${
                    timeframe === 'year' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                  }`}
                >
                  This Year
                </button>
                <button
                  onClick={() => setTimeframe('all')}
                  className={`px-4 py-2 rounded-lg ${
                    timeframe === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                  }`}
                >
                  All Time
                </button>
              </div>
            </div>
          </div>

          {/* Leaderboard Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Player
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Points
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Chores
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Streak
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Perfect Weeks
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bonuses
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(leaderboard) && leaderboard.map((entry, idx) => (
                  <tr key={entry.user_id || entry.id} className={idx === 0 ? 'bg-yellow-50' : idx === 1 ? 'bg-gray-50' : idx === 2 ? 'bg-orange-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getMedal(idx + 1)}
                        {getRankChange(entry.rank_change)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold mr-3"
                          style={{ backgroundColor: entry.color_theme }}
                        >
                          {entry.display_name[0]}
                        </div>
                        <div
                          className="cursor-pointer hover:text-blue-600"
                          onClick={() => loadPointBreakdown(entry.user_id || entry.id)}
                        >
                          <div className="text-sm font-medium text-gray-900">
                            {entry.display_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            @{entry.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-2xl font-bold text-blue-600">{entry.total_points}</div>
                      {entry.lifetime_points !== undefined && entry.lifetime_points !== entry.total_points && (
                        <div className="text-xs text-gray-500">Lifetime: {entry.lifetime_points}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-lg font-semibold">{entry.chores_completed}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        <Zap className="w-4 h-4 text-yellow-500 mr-1" />
                        <span className="font-semibold">{entry.streak_days}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        <Star className="w-4 h-4 text-purple-500 mr-1" />
                        <span>{entry.perfect_weeks}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-green-600 font-semibold">+{entry.bonus_earned}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Current Rewards Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Active Leaderboard Rewards</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['weekly', 'monthly', 'yearly'].map(period => {
                const periodRewards = rewards.filter(r => r.timeframe === period && r.is_active)
                return (
                  <div key={period} className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3 capitalize">{period} Rewards</h4>
                    <div className="space-y-2">
                      {periodRewards.length > 0 ? (
                        periodRewards.map(reward => (
                          <div key={reward.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center">
                              {reward.position === 1 && <Crown className="w-4 h-4 text-yellow-500 mr-2" />}
                              {reward.position === 2 && <Medal className="w-4 h-4 text-gray-400 mr-2" />}
                              {reward.position === 3 && <Medal className="w-4 h-4 text-orange-600 mr-2" />}
                              {reward.position > 3 && <span className="mr-2 text-gray-500">#{reward.position}</span>}
                              <span className="text-sm">{reward.description}</span>
                            </div>
                            <span className="text-sm font-semibold text-blue-600">
                              {reward.reward_type === 'points' ? `+${reward.reward_value} pts` : reward.reward_value}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No rewards configured</p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* Rewards Config Tab */}
      {activeTab === 'rewards' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold">Reward Configuration</h3>
            <button
              onClick={() => setShowRewardModal(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Reward
            </button>
          </div>

          {/* Rewards by Timeframe */}
          {['weekly', 'monthly', 'yearly'].map(period => {
            const periodRewards = rewards.filter(r => r.timeframe === period)
            return (
              <div key={period} className="mb-8">
                <h4 className="text-md font-semibold text-gray-700 mb-3 capitalize border-b pb-2">
                  {period} Rewards
                </h4>
                {periodRewards.length > 0 ? (
                  <div className="space-y-2">
                    {periodRewards.sort((a, b) => a.position - b.position).map(reward => (
                      <div key={reward.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center">
                            {reward.position === 1 && <Crown className="w-5 h-5 text-yellow-500" />}
                            {reward.position === 2 && <Medal className="w-5 h-5 text-gray-400" />}
                            {reward.position === 3 && <Medal className="w-5 h-5 text-orange-600" />}
                            {reward.position > 3 && <span className="text-gray-500 font-bold">#{reward.position}</span>}
                          </div>
                          <div>
                            <span className="font-medium">{reward.description}</span>
                            <div className="text-sm text-gray-500">
                              {reward.reward_type === 'points' ? `${reward.reward_value} points` :
                               reward.reward_type === 'prize' ? `Prize: ${reward.reward_value}` :
                               `Privilege: ${reward.reward_value}`}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleUpdateReward(reward.id, { is_active: !reward.is_active })}
                            className={`px-3 py-1 rounded text-sm ${
                              reward.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {reward.is_active ? 'Active' : 'Inactive'}
                          </button>
                          <button
                            onClick={() => handleDeleteReward(reward.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm py-4">No {period} rewards configured. Click "Add Reward" to create one.</p>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Distribution History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Distribution History</h3>

          {distributions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Position</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Points Earned</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Reward</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {distributions.map(dist => (
                    <tr key={dist.id}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {format(new Date(dist.distributed_at), 'MMM d, yyyy')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="capitalize font-medium">{dist.timeframe}</span>
                        <div className="text-xs text-gray-500">{dist.period_label}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{dist.user_name}</td>
                      <td className="px-4 py-3 text-center">
                        {dist.position === 1 && <Crown className="w-5 h-5 text-yellow-500 mx-auto" />}
                        {dist.position === 2 && <Medal className="w-5 h-5 text-gray-400 mx-auto" />}
                        {dist.position === 3 && <Medal className="w-5 h-5 text-orange-600 mx-auto" />}
                        {dist.position > 3 && <span className="text-gray-600">#{dist.position}</span>}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600">{dist.total_points}</td>
                      <td className="px-4 py-3 text-center text-sm">
                        {dist.points_awarded ? (
                          <span className="text-green-600 font-medium">+{dist.points_awarded} pts</span>
                        ) : dist.prize_description ? (
                          <span className="text-purple-600">{dist.prize_description}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {dist.prize_description ? (
                          dist.prize_fulfilled ? (
                            <span className="inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Fulfilled
                            </span>
                          ) : (
                            <button
                              onClick={() => handleFulfillPrize(dist.id)}
                              className="inline-flex items-center px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
                            >
                              <Clock className="w-3 h-3 mr-1" />
                              Mark Fulfilled
                            </button>
                          )
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Awarded
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <History className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No reward distributions yet.</p>
              <p className="text-sm mt-1">Distributions will appear here after rewards are given out.</p>
            </div>
          )}
        </div>
      )}

      {/* Auto-Distribution Settings Tab */}
      {activeTab === 'settings' && settings && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-6">Auto-Distribution Settings</h3>

          <div className="space-y-6">
            {/* Auto-distribution toggles */}
            <div className="border rounded-lg p-4">
              <h4 className="font-medium mb-4">Automatic Distribution</h4>
              <div className="space-y-4">
                {[
                  { key: 'auto_distribute_weekly', label: 'Weekly', desc: 'Automatically distribute rewards at the end of each week' },
                  { key: 'auto_distribute_monthly', label: 'Monthly', desc: 'Automatically distribute rewards at the end of each month' },
                  { key: 'auto_distribute_yearly', label: 'Yearly', desc: 'Automatically distribute rewards at the end of each year' }
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">{item.label}</span>
                      <p className="text-sm text-gray-500">{item.desc}</p>
                    </div>
                    <button
                      onClick={() => handleUpdateSettings({ [item.key]: !settings[item.key as keyof RewardSettings] })}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        settings[item.key as keyof RewardSettings] ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          settings[item.key as keyof RewardSettings] ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Distribution thresholds */}
            <div className="border rounded-lg p-4">
              <h4 className="font-medium mb-4">Distribution Rules</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Participants
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={settings.min_participants}
                    onChange={(e) => handleUpdateSettings({ min_participants: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum number of participants required to distribute rewards
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Points Threshold
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settings.min_points_threshold}
                    onChange={(e) => handleUpdateSettings({ min_points_threshold: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Winner must have at least this many points to receive reward
                  </p>
                </div>
              </div>
            </div>

            {/* Notification settings */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Notify on Distribution</span>
                  <p className="text-sm text-gray-500">Send notifications when rewards are distributed</p>
                </div>
                <button
                  onClick={() => handleUpdateSettings({ notify_on_distribution: !settings.notify_on_distribution })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    settings.notify_on_distribution ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      settings.notify_on_distribution ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Reward Modal */}
      {showRewardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add New Reward</h3>
              <button
                onClick={() => setShowRewardModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Position</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={newReward.position}
                  onChange={(e) => setNewReward({ ...newReward, position: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Timeframe</label>
                <select
                  value={newReward.timeframe}
                  onChange={(e) => setNewReward({ ...newReward, timeframe: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reward Type</label>
                <select
                  value={newReward.reward_type}
                  onChange={(e) => setNewReward({ ...newReward, reward_type: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="points">Points</option>
                  <option value="privilege">Privilege</option>
                  <option value="prize">Prize</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reward Value</label>
                <input
                  type="text"
                  value={newReward.reward_value}
                  onChange={(e) => setNewReward({ ...newReward, reward_value: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder={newReward.reward_type === 'points' ? '50' : 'Stay up 30min late'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input
                  type="text"
                  value={newReward.description}
                  onChange={(e) => setNewReward({ ...newReward, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Weekly winner bonus"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowRewardModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateReward}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Create Reward
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Point Breakdown Modal */}
      {selectedUser && pointBreakdown && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Point Breakdown for {pointBreakdown.display_name}
              </h3>
              <button
                onClick={() => {
                  setSelectedUser(null)
                  setPointBreakdown(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingBreakdown ? (
              <div className="text-center py-8">Loading...</div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-sm text-blue-600 mb-1">Chore Points</div>
                    <div className="text-2xl font-bold text-blue-900">
                      {pointBreakdown.chore_points}
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-sm text-green-600 mb-1">Bonus Points</div>
                    <div className="text-2xl font-bold text-green-900">
                      +{pointBreakdown.bonus_points}
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-sm text-red-600 mb-1">Deductions</div>
                    <div className="text-2xl font-bold text-red-900">
                      -{pointBreakdown.deductions}
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-sm text-purple-600 mb-1">Available</div>
                    <div className="text-2xl font-bold text-purple-900">
                      {pointBreakdown.available_points}
                    </div>
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Total Earned:</span>
                    <span className="text-xl font-bold">{pointBreakdown.total_earned}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Points Redeemed:</span>
                    <span className="text-xl text-orange-600">-{pointBreakdown.redemptions}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Points Converted (Cash):</span>
                    <span className="text-xl text-green-600">-{pointBreakdown.conversions}</span>
                  </div>
                </div>

                {/* Recent Activity */}
                {pointBreakdown.recent_activities && pointBreakdown.recent_activities.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3">Recent Activity</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {pointBreakdown.recent_activities.map((activity, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            {activity.type === 'earned' && <CheckCircle className="w-5 h-5 text-green-500" />}
                            {activity.type === 'deducted' && <AlertCircle className="w-5 h-5 text-red-500" />}
                            {activity.type === 'redeemed' && <Gift className="w-5 h-5 text-orange-500" />}
                            {activity.type === 'converted' && <DollarSign className="w-5 h-5 text-green-600" />}
                            <div>
                              <div className="text-sm font-medium">{activity.description}</div>
                              <div className="text-xs text-gray-500">{activity.date}</div>
                            </div>
                          </div>
                          <div className={`font-bold ${
                            activity.type === 'earned' ? 'text-green-600' :
                            activity.type === 'deducted' ? 'text-red-600' :
                            'text-gray-600'
                          }`}>
                            {activity.type === 'earned' ? '+' : '-'}{activity.points}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
